import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { AlertMessage } from '@/components/ui/AlertMessage';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { Pagination } from '@/components/ui/Pagination';
import { EmptyState } from '@/components/ui/EmptyState';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { getUserStatusBadge, getAuthTypeBadge } from '@/utils/badgeUtils';
import { adminAPI } from '@/services/api';
import { User } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { PAGINATION } from '@/constants';
import { useCustomers } from '@/hooks/common';
import { filterUsers, getRoleLabel, handleAPIError, getRoleOptions } from '@/utils';
import { SearchFilters, SearchFieldConfig } from '@/components/common/SearchFilters';

interface CustomerUsersTabProps {
  customerId: number | null;
  searchQuery: string; // 원본 값 (Input에 즉시 반영)
  debouncedSearchQuery?: string; // 디바운스된 값 (필요시 사용)
  onSearchChange: (query: string) => void;
  onBack: () => void;
  onSelectCustomer?: (customerId: number | null) => void;
  onEditUser: (user: User) => void;
  onPasswordChange: (user: User) => void;
  onAuthTypeChange: (user: User) => void;
  onApproveUser: (userId: number) => void | Promise<void>;
  onDeactivateUser: (user: User) => void | Promise<void>;
  onActivateUser?: (user: User) => void | Promise<void>;
  onDeleteUser?: (user: User) => void | Promise<void>;
  onRefresh?: () => void;  // 외부에서 리스트 새로고침을 트리거할 수 있는 콜백
  hideCustomerSelection?: boolean; // 고객사 선택 UI 숨김 여부 (내 이용자 관리용)
  approvingUserId?: number | null;  // 승인 진행 중인 사용자 ID
}

const CustomerUsersTab: React.FC<CustomerUsersTabProps> = ({
  customerId,
  searchQuery,
  debouncedSearchQuery: _debouncedSearchQuery,
  onSearchChange,
  onBack,
  onSelectCustomer,
  onEditUser,
  onPasswordChange,
  onAuthTypeChange,
  onApproveUser,
  onDeactivateUser,
  onActivateUser,
  onDeleteUser,
  onRefresh: _onRefresh,
  hideCustomerSelection = false,
  approvingUserId,
}) => {
  const { user: currentUser } = useAuth();
  const [customerUsers, setCustomerUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const pageSize = PAGINATION.USER_MANAGEMENT_PAGE_SIZE;
  
  // 고객사 목록 불러오기 (커스텀 훅 사용)
  const { customers } = useCustomers({ autoFetch: !hideCustomerSelection });

  const fetchCustomerUsers = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    setError('');
    try {
      const skip = (page - 1) * pageSize;
      const response = await adminAPI.getCustomerUsers(customerId, skip, pageSize);
      setCustomerUsers(response.users || []);
      setTotal(response.total || 0);
    } catch (error: any) {
      handleAPIError(error, '사용자 목록을 불러오는데 실패했습니다.', setError);
    } finally {
      setLoading(false);
    }
  }, [customerId, page, pageSize]);

  useEffect(() => {
    if (customerId) {
      fetchCustomerUsers();
    }
  }, [customerId, fetchCustomerUsers]);

  // onDeactivateUser가 호출되면 리스트 새로고침
  const handleDeactivateUserWrapper = async (user: User) => {
    await onDeactivateUser(user);
    // 비활성화 후 리스트 새로고침
    fetchCustomerUsers();
  };

  // 필터링 (통합 유틸리티 사용)
  const filteredUsers = useMemo(() => {
    return filterUsers(customerUsers, {
      query: searchQuery,
      status: statusFilter,
      role: roleFilter,
    });
  }, [customerUsers, searchQuery, statusFilter, roleFilter]);

  // 필터 값 객체 (SearchFilters 컴포넌트용)
  const filterValues = useMemo(() => ({
    customer: customerId ? String(customerId) : 'all',
    search: searchQuery,
    status: statusFilter,
    role: roleFilter,
  }), [customerId, searchQuery, statusFilter, roleFilter]);

  // 필터 필드 설정
  const filterFields: SearchFieldConfig[] = useMemo(() => [
    {
      key: 'customer',
      type: 'select',
      label: '고객사 선택',
      colSpan: 2,
      shouldShow: () => !hideCustomerSelection && !!onSelectCustomer,
      options: [
        { value: 'all', label: '전체 고객사' },
        ...customers.map(customer => ({
          value: String(customer.id),
          label: `${customer.customer_name} (${customer.customer_code})`,
        })),
      ],
      onChange: (value) => {
        if (onSelectCustomer) {
          onSelectCustomer(value === 'all' ? null : parseInt(value, 10));
        }
      },
    },
    {
      key: 'search',
      type: 'input',
      label: '이메일 또는 이름 검색',
      placeholder: '이메일 또는 이름으로 검색...',
      colSpan: !hideCustomerSelection && onSelectCustomer ? 4 : 6,
      onChange: (value) => {
        onSearchChange(value);
      },
    },
    {
      key: 'status',
      type: 'select',
      label: '상태',
      colSpan: 2,
      options: [
        { value: 'all', label: '전체' },
        { value: 'active', label: '활성화' },
        { value: 'pending', label: '승인대기중' },
        { value: 'invited', label: '초대 대기' },
        { value: 'deactivated', label: '비활성화' },
        { value: 'deleted', label: '삭제됨' },
      ],
      onChange: (value) => {
        setStatusFilter(value);
      },
    },
    {
      key: 'role',
      type: 'select',
      label: '역할',
      colSpan: 2,
      options: getRoleOptions().filter(opt => opt.value !== 'saas_admin').map(opt => ({
        value: opt.value,
        label: opt.label,
      })),
      onChange: (value) => {
        setRoleFilter(value);
      },
    },
  ], [hideCustomerSelection, onSelectCustomer, customers, onSearchChange, setStatusFilter, setRoleFilter]);

  // 필터 변경 핸들러
  const handleFilterChange = (key: string, value: any) => {
    if (key === 'customer' && onSelectCustomer) {
      onSelectCustomer(value === 'all' ? null : parseInt(value, 10));
    } else if (key === 'search') {
      onSearchChange(value);
    } else if (key === 'status') {
      setStatusFilter(value);
    } else if (key === 'role') {
      setRoleFilter(value);
    }
  };

  // 초기화 핸들러
  const handleReset = useCallback(() => {
    onSearchChange('');
    setStatusFilter('all');
    setRoleFilter('all');
  }, [onSearchChange, setStatusFilter, setRoleFilter]);

  // customerId가 없고 고객사 선택이 숨겨진 경우(기업관리자/내 이용자 관리), 자동으로 본인 고객사 설정
  useEffect(() => {
    if (hideCustomerSelection && !customerId && currentUser?.customer?.id && onSelectCustomer) {
      onSelectCustomer(currentUser.customer.id);
    }
  }, [hideCustomerSelection, customerId, currentUser?.customer?.id, onSelectCustomer]);

  if (!customerId) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle>전체 사용자</CardTitle>
          <CardDescription>
            기업 내 모든 사용자를 조회합니다.
          </CardDescription>
          </div>
          {/* 고객사 선택 UI는 hideCustomerSelection이 false일 때만 표시 */}
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">
              연결된 고객사가 없습니다.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>전체 사용자</CardTitle>
        <CardDescription>
          기업 내 모든 사용자를 조회하고 권한/상태를 관리합니다.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* 검색 및 필터 - 공통 컴포넌트 사용 */}
        <SearchFilters
          fields={filterFields}
          values={filterValues}
          onChange={handleFilterChange}
          onReset={handleReset}
          onSearch={() => {}} // 검색 버튼 표시 (클라이언트 사이드 필터링이므로 별도 동작 불필요)
          loading={loading}
          showSearchButton={true}
        />
      {error && (
        <AlertMessage 
          type="error" 
          message={error} 
          onClose={() => {}} 
        />
      )}
      {loading && customerUsers.length === 0 ? (
        <div className="flex items-center justify-center h-32">
          <LoadingSpinner size="md" message="사용자를 불러오는 중..." />
        </div>
      ) : (
        <>
          {/* "고객사 선택으로 돌아가기" 버튼은 hideCustomerSelection이 false일 때만 표시 */}
          {!hideCustomerSelection && onBack && (
            <div className="p-4 border-b border-gray-200">
              <Button
                variant="outline"
                onClick={onBack}
              >
                ← 고객사 선택으로 돌아가기
              </Button>
            </div>
          )}
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                  <TableRow>
                  {/* 고객사 컬럼은 hideCustomerSelection이 false일 때만 표시 */}
                  {!hideCustomerSelection && <TableHead>고객사</TableHead>}
                  <TableHead>이메일</TableHead>
                  <TableHead>이름</TableHead>
                  <TableHead>상태</TableHead>
                  <TableHead>역할</TableHead>
                  <TableHead>로그인</TableHead>
                  <TableHead className="text-right">작업</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={!hideCustomerSelection ? 7 : 6} className="text-center py-8">
                      <EmptyState
                        message={searchQuery ? '검색 결과가 없습니다.' : '사용자가 없습니다.'}
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => (
                    <TableRow key={user.id}>
                      {/* 고객사 컬럼은 hideCustomerSelection이 false일 때만 표시 */}
                      {!hideCustomerSelection && (
                        <TableCell className="text-sm text-gray-500">
                          {user.customer?.customer_name || '(미할당)'}
                        </TableCell>
                      )}
                      <TableCell className="text-sm font-medium">
                        {user.email}
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {user.name || '-'}
                      </TableCell>
                      <TableCell>
                        {getUserStatusBadge(user.status)}
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {user.role === 'user' && user.permission === 'readonly' 
                          ? '읽기 전용' 
                          : getRoleLabel(user.role)}
                      </TableCell>
                      <TableCell>
                        <span
                          className="cursor-pointer hover:opacity-80"
                          onClick={() => onAuthTypeChange(user)}
                          title="클릭하여 로그인 유형 변경"
                        >
                          {getAuthTypeBadge(user.auth_type)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end space-x-2">
                          {user.auth_type !== 'oauth' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => onPasswordChange(user)}
                            >
                              비밀번호
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onEditUser(user)}
                          >
                            수정
                          </Button>
                          {user.status === 'pending' && (
                            <Button
                              size="sm"
                              disabled={approvingUserId === user.id}
                              onClick={async () => {
                                try {
                                  await onApproveUser(user.id);
                                  await fetchCustomerUsers();
                                } catch (error) {
                                  // 에러는 상위 컴포넌트에서 처리
                                }
                              }}
                            >
                              {approvingUserId === user.id ? '승인 중...' : '승인'}
                            </Button>
                          )}
                          {user.status === 'active' && (
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeactivateUserWrapper(user)}
                            >
                              비활성화
                            </Button>
                          )}
                          {user.status === 'deactivated' && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={async () => {
                                  if (onActivateUser) {
                                    await onActivateUser(user);
                                  } else {
                                    // fallback: 직접 활성화 (하위 호환성)
                                    try {
                                      await onApproveUser(user.id);
                                      await fetchCustomerUsers();
                                    } catch {
                                      // 상위에서 에러 처리
                                    }
                                  }
                                }}
                              >
                                활성화
                              </Button>
                              {onDeleteUser && (
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => onDeleteUser(user)}
                                >
                                  삭제
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          
          <Pagination
            currentPage={page}
            totalPages={Math.ceil(total / pageSize)}
            totalItems={total}
            itemsPerPage={pageSize}
            onPageChange={(newPage) => setPage(newPage)}
            showItemsPerPage={false}
            showTotal={true}
            className="mt-4"
          />
        </>
      )}
      </CardContent>
    </Card>
  );
};

export default CustomerUsersTab;

